import React, { useState } from 'react';
import {
  Row, Col, Card, Form, Input, Button, message, Typography,
  Space, Tag, Alert, InputNumber, Table
} from 'antd';
import {
  DatabaseOutlined, CheckCircleOutlined, SettingOutlined,
  CloudServerOutlined, ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const SCHEMA_TABLES = [
  { name: 'users',                purpose: 'System users & authentication' },
  { name: 'events',               purpose: 'Conference / Exhibition events' },
  { name: 'delegate_categories',  purpose: 'Delegate category master' },
  { name: 'conference_locations', purpose: 'Halls, zones, areas' },
  { name: 'rfid_readers',         purpose: 'RFID reader configuration' },
  { name: 'delegates',            purpose: 'Conference delegate registry' },
  { name: 'raw_rfid_scans',       purpose: 'Raw scan data from readers' },
  { name: 'delegate_movements',   purpose: 'Delegate location history' },
  { name: 'client_push_configs',  purpose: 'Client API push configurations' },
];

export default function DatabaseConfig() {
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult]   = useState(null);
  const [form] = Form.useForm();

  const defaults = {
    host:     'localhost',
    port:     3306,
    database: 'rfid_delegate_db',
    user:     'rfid_user',
    charset:  'utf8mb4',
    timezone: 'Asia/Kolkata',
    pool_min: 2,
    pool_max: 10,
  };

  const testConnection = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const r = await axios.get('/api/health');
      setTestResult({ success: true, message: `Connected — ${r.data.system} v${r.data.version}`, latency: '< 10ms' });
    } catch (e) {
      setTestResult({ success: false, message: 'Cannot reach server: ' + (e.message || 'Unknown error') });
    } finally {
      setTestLoading(false);
    }
  };

  const schemaColumns = [
    { title: 'Table Name', dataIndex: 'name', render: v => <Text code>{v}</Text> },
    { title: 'Purpose',    dataIndex: 'purpose' },
    { title: 'Status',     render: () => <Tag color="green"><CheckCircleOutlined /> Active</Tag>, width: 120 },
  ];

  return (
    <div>
      <Row align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <DatabaseOutlined style={{ marginRight: 8 }} />Database Configuration
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Manage MySQL database connection settings and schema</Text>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card title={<><DatabaseOutlined /> Connection Settings</>} style={{ borderRadius: 10 }}>
            <Alert
              message="Configuration is stored in server/.env file. Use this panel to view and test the connection."
              type="info" showIcon style={{ marginBottom: 16 }}
            />
            <Form form={form} layout="vertical" initialValues={defaults}>
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item name="host" label="Database Host">
                    <Input prefix={<CloudServerOutlined />} placeholder="localhost" readOnly />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="port" label="Port">
                    <InputNumber style={{ width: '100%' }} readOnly />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="database" label="Database Name">
                    <Input readOnly />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="user" label="Username">
                    <Input readOnly />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Password">
                    <Input.Password placeholder="(from .env file)" readOnly />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="charset" label="Charset">
                    <Input readOnly />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="pool_min" label="Pool Min">
                    <InputNumber style={{ width: '100%' }} readOnly />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="pool_max" label="Pool Max">
                    <InputNumber style={{ width: '100%' }} readOnly />
                  </Form.Item>
                </Col>
              </Row>
              <Space>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={testConnection} loading={testLoading}
                  style={{ background: '#0F1D6B' }}>
                  Test Connection
                </Button>
                <Button icon={<ReloadOutlined />} onClick={() => message.info('Run: node database/setup.js on the server')}>
                  Run Schema Setup
                </Button>
              </Space>
            </Form>

            {testResult && (
              <Alert
                style={{ marginTop: 16 }}
                type={testResult.success ? 'success' : 'error'}
                showIcon
                message={testResult.success ? 'Connection Successful' : 'Connection Failed'}
                description={<>
                  <div>{testResult.message}</div>
                  {testResult.latency && <div style={{ color: '#52c41a' }}>Latency: {testResult.latency}</div>}
                </>}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title={<><SettingOutlined /> System Info</>} style={{ borderRadius: 10, marginBottom: 16 }}>
            {[
              { label: 'Database Engine', value: 'MySQL 8.0+' },
              { label: 'ORM',            value: 'mysql2 (Node.js)' },
              { label: 'API Server',     value: 'Express.js 4.x' },
              { label: 'Auth',           value: 'JWT (24h expiry)' },
              { label: 'WebSocket',      value: 'ws (port /ws)' },
              { label: 'Server Port',    value: '5010' },
            ].map((row, i) => (
              <Row key={i} justify="space-between" style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{row.label}</Text>
                <Text style={{ fontSize: 12, fontWeight: 600 }}>{row.value}</Text>
              </Row>
            ))}
          </Card>

          <Card title={<><SettingOutlined /> Server .env Template</>} style={{ borderRadius: 10 }}>
            <pre style={{ background: '#0F1D6B', color: '#fff', padding: 12, borderRadius: 8, fontSize: 11, overflowX: 'auto' }}>
{`PORT=5010
DB_HOST=localhost
DB_USER=rfid_user
DB_PASSWORD=Rfid@Identium2026!
DB_NAME=rfid_delegate_db
JWT_SECRET=identium_rfid_secret_2026
JWT_EXPIRES_IN=24h
APP_URL=http://213.210.21.60:3010`}
            </pre>
            <Text type="secondary" style={{ fontSize: 11 }}>Save as /backend/.env and restart the server</Text>
          </Card>
        </Col>
      </Row>

      <Card title={<><DatabaseOutlined /> Database Schema Tables</>} style={{ borderRadius: 10, marginTop: 24 }}>
        <Table dataSource={SCHEMA_TABLES} columns={schemaColumns} rowKey="name" size="small" pagination={false} />
      </Card>
    </div>
  );
}
